import { engageKeys } from "../../engage/shared/engage-keys";
import { useWorkspaceId } from "../../../session/workspace-context";
import { serviceKeys } from "../service-keys";
import { useServiceMutation, useServiceQuery } from "../use-service-query";
import type { LeadConvertBody } from "./lead-convert-types";

/** `POST /v1/leads/{id}/convert-preview`: the organisation and contact that would be made, and the duplicates found. Changes nothing. */
export function useLeadConvertPreview(leadId: string) {
  return useServiceMutation({
    request: (api, body: LeadConvertBody) => api.POST("/v1/leads/{lead_id}/convert-preview", { params: { path: { lead_id: leadId } }, body }),
  });
}

/** `POST /v1/leads/{id}/convert`: makes (or links) the organisation and the contact, once. The lead, the organisations and the contacts are read again. */
export function useConvertLead(leadId: string) {
  return useServiceMutation({
    request: (api, body: LeadConvertBody) => api.POST("/v1/leads/{lead_id}/convert", { params: { path: { lead_id: leadId } }, body }),
    invalidate: (ws) => [engageKeys.leads(ws), serviceKeys.organisations(ws), serviceKeys.contacts(ws)],
  });
}

/** The lead an organisation came from (404 when it was not converted from one). */
export function useOriginatingLead(organisationId: string, enabled: boolean) {
  const ws = useWorkspaceId();
  return useServiceQuery({
    queryKey: serviceKeys.originatingLead(ws, organisationId),
    enabled,
    staleTime: 60_000,
    request: (api, signal) => api.GET("/v1/organisations/{organisation_id}/originating-lead", { params: { path: { organisation_id: organisationId } }, signal }),
  });
}

/** The organisation a lead was converted to, for the link on the lead (needs `organisations.view`). */
export function useConvertedOrganisation(organisationId: string | null, enabled: boolean) {
  const ws = useWorkspaceId();
  const id = organisationId ?? "none";
  return useServiceQuery({
    queryKey: serviceKeys.organisation(ws, id),
    enabled: enabled && organisationId !== null,
    staleTime: 60_000,
    request: (api, signal) => api.GET("/v1/organisations/{organisation_id}", { params: { path: { organisation_id: id } }, signal }),
  });
}
